/**
 * Discrete calculation/factor sub-views for the activity detail surfaces.
 *
 * Three independent exports — each renders one self-contained block:
 *   - ActivityFactorView: emission-factor banners, table, "View details"
 *     panel, and the Change-factor modal.
 *   - ActivityMethodologyView: GWP / version / calculated-at table.
 *   - ActivityReferencesView: GHG-protocol links.
 *
 * Drawer and full-page detail compose them in their own layouts.
 * No `hide*` flags — each consumer renders only what it needs.
 */

import { useMemo, useState } from 'react';
import { Table } from 'reactstrap';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { useGetInitiativeUsersQuery } from '../../../../api/manage-users';
import { useOverrideCalculationFactorMutation } from '../../api/emissions';
import { type ActivityRecord, type EmissionCalculation, EmissionScope } from '../../types/emissions';
import { REGION_OPTIONS } from '../form/types';
import { FactorSearchModal } from '../factor-search/FactorSearchModal';
import { getActivityUnitType } from '../factor-search/getActivityUnitType';
import {
  FUEL_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
} from '@g17eco/emissions-config';
import {
  formatDate,
  formatDateShort,
  formatProvider,
  formatSource,
  hasDetails,
  SCOPE2_METHOD_LABELS,
} from './formatters';

export type CalculationActivityData = Pick<
  ActivityRecord,
  | 'region'
  | 'bulkImportId'
  | 'activityType'
  | 'unit'
  | 'fuelType'
  | 'vehicleType'
  | 'refrigerantType'
  | 'processType'
>;

// ─── Emission factor view ────────────────────────────────────────────────

interface ActivityFactorViewProps {
  calculation: EmissionCalculation;
  activityData?: CalculationActivityData;
  initiativeId: string;
  activityId: string;
}

/**
 * The emission-factor block: status banners (override / fallback), the
 * factor table (or empty-state if no factor matched), the "View details"
 * panel, and the embedded Change-factor modal.
 */
export const ActivityFactorView = ({
  calculation,
  activityData,
  initiativeId,
  activityId,
}: ActivityFactorViewProps) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const { data = { users: [] } } = useGetInitiativeUsersQuery(
    { initiativeId, includeSubsidiaries: false },
    { skip: !calculation.isFactorOverride },
  );
  const user = useMemo(
    () => data.users.find((u) => u._id === calculation.factorOverriddenBy),
    [data.users, calculation.factorOverriddenBy],
  );

  const [overrideFactor] = useOverrideCalculationFactorMutation();

  return (
    <div>
      {calculation.isFactorOverride && (
        <div className='tw:rounded-lg tw:border tw:border-blue-200 tw:bg-blue-50 tw:p-2.5 tw:text-xs tw:text-blue-800 tw:mb-3 tw:flex tw:items-center tw:gap-2'>
          <i className='fa fa-user-edit' />
          <span>
            <strong>Manually overridden</strong>
            {calculation.factorOverriddenBy && user && <> by {user.firstName} {user.surname}</>}
            {calculation.factorOverriddenAt && <> on {formatDateShort(calculation.factorOverriddenAt)}</>}
          </span>
        </div>
      )}

      {calculation.fallbackType && (
        <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-2.5 tw:text-xs tw:text-amber-800 tw:mb-3 tw:flex tw:items-center tw:gap-2'>
          <i className='fa fa-exchange-alt' />
          <span>
            <strong>Fallback used:</strong>{' '}
            {calculation.fallbackType === 'year' && 'Exact year not available — used nearest year.'}
            {calculation.fallbackType === 'region' && 'Exact region not available — used broader region.'}
            {calculation.fallbackType === 'region_and_year' && 'Exact region and year not available — used broader match.'}
          </span>
        </div>
      )}

      {calculation.emissionFactorSource === 'none' ? (
        <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-3 tw:text-sm tw:text-amber-800'>
          <p className='tw:font-medium tw:mb-1'>No emission factor found</p>
          <p className='tw:text-xs tw:mb-3 tw:text-amber-700'>
            No matching emission factor was found for this activity, region, and unit combination.
            Search Climatiq for a compatible factor to calculate emissions.
          </p>
          <div className='tw:flex tw:justify-end tw:gap-2'>
            <Button variant='amber' onClick={() => setIsSearchModalOpen(true)}>
              <i className='fa fa-search tw:mr-1' />
              Search factors
            </Button>
          </div>
        </div>
      ) : (
        <>
          {calculation.emissionFactorDescription && (
            <p className='tw:text-xs tw:text-muted-foreground tw:mb-3 tw:leading-relaxed'>
              {calculation.emissionFactorDescription}
            </p>
          )}
          <Table size='sm' borderless className='mb-0'>
            <tbody>
              {calculation.emissionFactorName && (
                <tr>
                  <td className='text-ThemeTextLight ps-0' style={{ width: '40%' }}>Name</td>
                  <td className='text-end pe-0'>{calculation.emissionFactorName}</td>
                </tr>
              )}
              <tr>
                <td className='text-ThemeTextLight ps-0' style={{ width: '40%' }}>Factor</td>
                <td className='text-end pe-0'>
                  {calculation.emissionFactorValue.toFixed(4)}{' '}
                  <span className='text-ThemeTextLight'>{calculation.emissionFactorUnit}</span>
                </td>
              </tr>
              <tr>
                <td className='text-ThemeTextLight ps-0'>Source</td>
                <td className='text-end pe-0'>
                  <Badge variant='default'>{formatSource(calculation.emissionFactorSource)}</Badge>
                </td>
              </tr>
              {(calculation.emissionFactorSector || calculation.emissionFactorCategory) && (
                <tr>
                  <td className='text-ThemeTextLight ps-0'>Taxonomy</td>
                  <td className='text-end pe-0'>
                    <div className='tw:inline-flex tw:flex-wrap tw:gap-1.5 tw:justify-end'>
                      {calculation.emissionFactorSector && (
                        <Badge variant='secondary' className='tw:text-[0.65rem]'>
                          {calculation.emissionFactorSector}
                        </Badge>
                      )}
                      {calculation.emissionFactorCategory &&
                        calculation.emissionFactorCategory !== calculation.emissionFactorSector && (
                          <Badge variant='secondary' className='tw:text-[0.65rem]'>
                            {calculation.emissionFactorCategory}
                          </Badge>
                        )}
                    </div>
                  </td>
                </tr>
              )}
              <tr>
                <td className='text-ThemeTextLight ps-0'>Region</td>
                <td className='text-end pe-0'>
                  <Badge variant='secondary'>
                    {calculation.emissionFactorRegion ?? activityData?.region ?? 'Global'}
                  </Badge>
                  {calculation.emissionFactorRegionName && (
                    <span className='text-ThemeTextLight tw:ml-2 tw:text-xs'>
                      {calculation.emissionFactorRegionName}
                    </span>
                  )}
                  {calculation.emissionFactorRegion &&
                    activityData?.region &&
                    calculation.emissionFactorRegion !== activityData.region && (
                      <Badge
                        variant='secondary'
                        className='tw:ml-2 tw:bg-amber-500/10 tw:text-amber-700 tw:border-amber-200 tw:text-[0.65rem]'
                      >
                        fallback from {activityData.region}
                      </Badge>
                    )}
                </td>
              </tr>
              {calculation.emissionFactorYear && (
                <tr>
                  <td className='text-ThemeTextLight ps-0'>Year</td>
                  <td className='text-end pe-0'>{calculation.emissionFactorYear}</td>
                </tr>
              )}
              {calculation.emissionFactorUnitType && calculation.emissionFactorUnitType.length > 0 && (
                <tr>
                  <td className='text-ThemeTextLight ps-0'>Unit Type</td>
                  <td className='text-end pe-0'>{calculation.emissionFactorUnitType.join(', ')}</td>
                </tr>
              )}
              {calculation.emissionFactorProvider && (
                <tr>
                  <td className='text-ThemeTextLight ps-0'>Provider</td>
                  <td className='text-end pe-0'>
                    <Badge variant='secondary' className='tw:text-[0.65rem] tw:py-0 tw:border-border'>
                      {formatProvider(calculation.emissionFactorProvider)}
                    </Badge>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <div className='tw:mt-3 tw:flex tw:justify-end tw:gap-2'>
            {hasDetails(calculation) && (
              <Button
                variant={isDetailsOpen ? 'default' : 'outline'}
                size='xs'
                className='tw:text-xs'
                onClick={() => setIsDetailsOpen((open) => !open)}
              >
                {isDetailsOpen ? 'Hide details' : 'View details'}
                <i
                  className={`fa fa-chevron-${isDetailsOpen ? 'up' : 'down'} tw:ms-1`}
                  style={{ fontSize: '0.55rem' }}
                />
              </Button>
            )}
            <Button
              variant='outline'
              size='xs'
              className='tw:text-xs'
              onClick={() => setIsSearchModalOpen(true)}
            >
              <i className='fa fa-search tw:me-1' />
              Change factor
            </Button>
          </div>

          {isDetailsOpen && (
            <div className='tw:mt-2 tw:rounded tw:border tw:border-border tw:bg-muted tw:p-3 tw:text-sm'>
              {calculation.emissionFactorSourceLink && (
                <div className='tw:mb-1'>
                  <a href={calculation.emissionFactorSourceLink} target='_blank' rel='noopener noreferrer'>
                    {calculation.emissionFactorSourceDataset || 'View source'}
                  </a>
                </div>
              )}
              {(calculation.emissionFactorId || calculation.climatiqFactorId || calculation.climatiqActivityId) && (
                <div className='tw:pt-2 tw:border-t tw:border-border text-ThemeTextLight tw:text-xs'>
                  {calculation.emissionFactorId && (
                    <div className='tw:mb-1'>
                      Local factor ID: <code>{calculation.emissionFactorId}</code>
                    </div>
                  )}
                  {calculation.climatiqFactorId && (
                    <div className='tw:mb-1'>
                      Factor: <code>{calculation.climatiqFactorId}</code>
                    </div>
                  )}
                  {calculation.climatiqActivityId && (
                    <div>
                      Activity: <code>{calculation.climatiqActivityId}</code>
                    </div>
                  )}
                </div>
              )}
              {calculation.sourceTrail && calculation.sourceTrail.length > 0 && (
                <div className='tw:pt-2 tw:border-t tw:border-border'>
                  <div className='text-ThemeTextLight tw:text-xs tw:font-semibold tw:mb-1'>Source Trail</div>
                  {calculation.sourceTrail.map((entry, i) => (
                    <div key={i} className='tw:text-xs tw:mb-1.5'>
                      <div className='tw:font-medium'>{entry.name}</div>
                      <div className='text-ThemeTextLight'>
                        {entry.source}{entry.sourceDataset ? ` — ${entry.sourceDataset}` : ''}
                        {entry.year ? ` (${entry.year})` : ''}
                        {entry.region ? ` · ${entry.region}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <FactorSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        initiativeId={initiativeId}
        scope={calculation.scope}
        activityType={activityData?.activityType ?? ''}
        unitType={getActivityUnitType(activityData?.unit)}
        initialQuery={buildInitialFactorQuery(calculation, activityData)}
        initialRegion={calculation.emissionFactorRegion ?? activityData?.region}
        initialYear={calculation.emissionFactorYear}
        currentFactorId={calculation.climatiqFactorId}
        regions={REGION_OPTIONS}
        onSelect={async (factor) => {
          const result = await overrideFactor({
            initiativeId,
            activityId,
            climatiqFactorId: factor.id,
          });
          if ('error' in result) {
            throw new Error(
              'Failed to apply this factor. The unit or region may not be compatible with this activity.',
            );
          }
        }}
      />
    </div>
  );
};

// ─── Methodology table ────────────────────────────────────────────────────

interface ActivityMethodologyViewProps {
  calculation: EmissionCalculation;
  activityData?: CalculationActivityData;
}

export const ActivityMethodologyView = ({ calculation, activityData }: ActivityMethodologyViewProps) => (
  <Table size='sm' borderless className='mb-0'>
    <tbody>
      <tr>
        <td className='text-ThemeTextLight ps-0' style={{ width: '40%' }}>GWP Methodology</td>
        <td className='text-end pe-0'>{calculation.gwpMethodology}</td>
      </tr>
      {calculation.scope2Method && (
        <tr>
          <td className='text-ThemeTextLight ps-0'>Scope 2 Method</td>
          <td className='text-end pe-0'>{SCOPE2_METHOD_LABELS[calculation.scope2Method]}</td>
        </tr>
      )}
      <tr>
        <td className='text-ThemeTextLight ps-0'>Calculated At</td>
        <td className='text-end pe-0'>{formatDate(calculation.calculatedAt)}</td>
      </tr>
      {activityData?.bulkImportId && (
        <tr>
          <td className='text-ThemeTextLight ps-0'>Input Source</td>
          <td className='text-end pe-0'>
            <Badge variant='secondary' className='ms-1 text-ThemeLight tw:rounded-full'>
              <i className='fa fa-file-import me-1' />
              Bulk file upload
            </Badge>
          </td>
        </tr>
      )}
      <tr>
        <td className='text-ThemeTextLight ps-0'>Version</td>
        <td className='text-end pe-0'>v{calculation.version}</td>
      </tr>
    </tbody>
  </Table>
);

// ─── References & Standards ───────────────────────────────────────────────

interface ActivityReferencesViewProps {
  calculation: EmissionCalculation;
}

export const ActivityReferencesView = ({ calculation }: ActivityReferencesViewProps) => (
  <ul className='list-unstyled mb-0'>
    <li className='tw:mb-1 tw:text-sm'>
      <a
        href='https://ghgprotocol.org/corporate-standard'
        target='_blank'
        rel='noopener noreferrer'
        className='text-decoration-none'
      >
        • GHG Protocol Corporate Accounting and Reporting Standard
      </a>
    </li>
    {calculation.scope === EmissionScope.Scope2 && (
      <li className='tw:text-sm'>
        <a
          href='https://ghgprotocol.org/scope_2_guidance'
          target='_blank'
          rel='noopener noreferrer'
          className='text-decoration-none'
        >
          • GHG Protocol Scope 2 Guidance
        </a>
      </li>
    )}
  </ul>
);

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Pick the most useful prefilled query for the factor-search modal.
 * Prefers the human factor name (e.g. "Emission intensity of propane (net CV)")
 * since it's the closest match to whatever was already selected; falls back
 * to the activity's discriminator (fuel/refrigerant/gas/process) label,
 * then the activity-type label as a last resort.
 */
const buildInitialFactorQuery = (
  calculation: EmissionCalculation,
  activityData?: CalculationActivityData,
): string => {
  if (calculation.emissionFactorName) return calculation.emissionFactorName;
  if (activityData?.fuelType) return FUEL_TYPE_LABELS[activityData.fuelType] ?? activityData.fuelType;
  if (activityData?.refrigerantType) return GAS_TYPE_LABELS[activityData.refrigerantType] ?? activityData.refrigerantType;
  if (activityData?.processType) return PROCESS_TYPE_LABELS[activityData.processType] ?? activityData.processType;
  if (activityData?.vehicleType) return VEHICLE_TYPE_LABELS[activityData.vehicleType] ?? activityData.vehicleType;
  return (activityData?.activityType ?? '').replace(/_/g, ' ');
};
